import { useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Check,
  Clock,
  Dumbbell,
  Flame,
  Lock,
  Play,
  Search,
} from "lucide-react";
import type { Level, LevelId, UserProgress } from "../../lib/gym/types";
import { trainingPlan } from "../../lib/gym/trainingPlan";
import {
  getCategoryLabel,
  getDifficultyLabel,
  getTypeLabel,
} from "../../lib/gym/trainingPlan";
import { formatPracticeDuration } from "../../lib/gym/progressMetrics";
import {
  getCompletedCount,
  getTotalExerciseCount,
  isLevelCompleted,
  isLevelUnlocked,
} from "../../lib/gym/gymProgress";
import { GYM_STUDIO } from "../../lib/gym/gymStudioTheme";
import { BEGINNER_PATH_THEME as PATH } from "../../lib/beginnerPath/beginnerPathTheme";
import {
  BEGINNER_PATH_EXERCISE_GRID_CLASS,
  BEGINNER_PATH_GYM_LAYOUT_CLASS,
} from "../../lib/beginnerPath/beginnerPathLayout";
import {
  GuitarBasicsReferenceSheet,
  GuitarBasicsReferenceTrigger,
} from "./GuitarBasicsReferenceSheet";

export interface GymLibraryProps {
  progress: UserProgress;
  onSelectExercise: (exerciseId: string) => void;
  /** Misma visual que Camino principiante (Serenity). */
  pathMode?: boolean;
  focusLevelId?: LevelId | null;
}

export function GymLibrary({
  progress,
  onSelectExercise,
  pathMode = false,
  focusLevelId = null,
}: GymLibraryProps) {
  const pathMint = "#99f6e4";
  const [search, setSearch] = useState("");
  const [activeLevelId, setActiveLevelId] = useState<LevelId>(focusLevelId ?? "aprendiz");
  const [referenceOpen, setReferenceOpen] = useState(false);

  const visibleLevels = focusLevelId
    ? trainingPlan.filter((l) => l.id === focusLevelId)
    : trainingPlan;

  const totalExercises = focusLevelId
    ? visibleLevels[0]?.exercises.length ?? 0
    : getTotalExerciseCount();
  const activeLevel = trainingPlan.find((l) => l.id === activeLevelId) ?? trainingPlan[0];

  const filteredExercises = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return activeLevel.exercises;
    return activeLevel.exercises.filter(
      (ex) =>
        ex.title.toLowerCase().includes(q) ||
        ex.subtitle.toLowerCase().includes(q) ||
        getCategoryLabel(ex.category).toLowerCase().includes(q),
    );
  }, [activeLevel.exercises, search]);

  return (
    <div className={`h-full flex flex-col font-sans ${pathMode ? "min-h-0" : ""}`}>
      {!pathMode && (
      <div
        className="shrink-0 px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4 border-b"
        style={{ borderColor: GYM_STUDIO.border }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-xl flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${GYM_STUDIO.accentSoft}, transparent)`,
              border: `1px solid ${GYM_STUDIO.border}`,
            }}
          >
            <Dumbbell className="w-5 h-5" style={{ color: GYM_STUDIO.accent }} />
          </div>
          <div>
            <h1
              className="font-serif text-xl font-semibold tracking-tight"
              style={{ color: GYM_STUDIO.textMain }}
            >
              Gimnasio
            </h1>
            <p className="text-xs" style={{ color: GYM_STUDIO.textDim }}>
              {progress.completedExercises.length}/{totalExercises} ejercicios · método chileno
            </p>
          </div>
        </div>

        <GymStatsToolbar
          progress={progress}
          search={search}
          onSearchChange={setSearch}
          pathMode={false}
        />
      </div>
      )}

      {pathMode && (
        <div className="shrink-0 w-full pb-4">
          <GymStatsToolbar
            progress={progress}
            search={search}
            onSearchChange={setSearch}
            pathMode
            subtitle={`${progress.completedExercises.length}/${totalExercises} ejercicios en este paso`}
            onOpenReference={() => setReferenceOpen(true)}
          />
        </div>
      )}

      {!focusLevelId && (
      <div
        className="shrink-0 w-full py-4 border-b overflow-x-auto"
        style={{ borderColor: pathMode ? PATH.cardBorder : GYM_STUDIO.border }}
      >
        <div className="flex gap-2 min-w-max pb-1">
          {visibleLevels.map((level, idx) => {
            const levelIndex = trainingPlan.findIndex((l) => l.id === level.id);
            return (
            <LevelChip
              key={level.id}
              level={level}
              levelIndex={levelIndex}
              progress={progress}
              active={level.id === activeLevelId}
              onSelect={() => setActiveLevelId(level.id)}
              pathMode={pathMode}
            />
            );
          })}
        </div>
      </div>
      )}

      <div className={`flex-1 overflow-y-auto py-2 ${pathMode ? "w-full" : "px-6 sm:px-8 py-6"}`}>
        {pathMode ? (
          <div className={BEGINNER_PATH_GYM_LAYOUT_CLASS}>
            <LevelHero level={activeLevel} progress={progress} pathMode />
            <div className={BEGINNER_PATH_EXERCISE_GRID_CLASS}>
              <AnimatePresence mode="popLayout">
                {filteredExercises.map((exercise) => (
                  <ExerciseCard
                    key={exercise.id}
                    exercise={exercise}
                    levelColor={pathMint}
                    completed={progress.completedExercises.includes(exercise.id)}
                    score={progress.scores[exercise.id]}
                    onOpen={() => onSelectExercise(exercise.id)}
                    pathMode
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        ) : (
          <>
        <LevelHero level={activeLevel} progress={progress} pathMode={false} />

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 max-w-[1600px] mx-auto mt-6">
          <AnimatePresence mode="popLayout">
            {filteredExercises.map((exercise) => (
              <ExerciseCard
                key={exercise.id}
                exercise={exercise}
                levelColor={activeLevel.color}
                completed={progress.completedExercises.includes(exercise.id)}
                score={progress.scores[exercise.id]}
                onOpen={() => onSelectExercise(exercise.id)}
                pathMode={false}
              />
            ))}
          </AnimatePresence>
        </div>
          </>
        )}

        {filteredExercises.length === 0 && (
          <p
            className="text-center text-sm mt-12"
            style={{ color: pathMode ? PATH.textMuted : GYM_STUDIO.textMuted }}
          >
            Sin ejercicios para esta búsqueda
          </p>
        )}
      </div>

      <GuitarBasicsReferenceSheet
        open={referenceOpen}
        onClose={() => setReferenceOpen(false)}
        pathMode={pathMode}
      />
    </div>
  );
}

function GymStatsToolbar({
  progress,
  search,
  onSearchChange,
  pathMode,
  subtitle,
  onOpenReference,
}: {
  progress: UserProgress;
  search: string;
  onSearchChange: (v: string) => void;
  pathMode: boolean;
  subtitle?: string;
  onOpenReference?: () => void;
}) {
  const border = pathMode ? PATH.cardBorder : GYM_STUDIO.border;
  const bg = pathMode ? PATH.cardBg : GYM_STUDIO.bgElevated;
  const textMuted = pathMode ? PATH.textMuted : GYM_STUDIO.textMuted;
  const textMain = pathMode ? PATH.text : GYM_STUDIO.textMain;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;

  return (
    <div className={`flex flex-wrap items-center gap-3 ${pathMode ? "justify-end lg:justify-between w-full" : ""}`}>
      {subtitle && (
        <p className="text-xs w-full sm:w-auto lg:flex-1" style={{ color: textMuted }}>
          {subtitle}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3 ml-auto">
      {onOpenReference && (
        <GuitarBasicsReferenceTrigger onClick={onOpenReference} pathMode={pathMode} />
      )}
      <div
        className="flex items-center gap-3 px-3 py-2 rounded-xl text-xs"
        style={{ backgroundColor: bg, border: `1px solid ${border}`, color: textMuted }}
      >
        <span className="inline-flex items-center gap-1">
          <Flame className="w-3.5 h-3.5 text-orange-400" />
          {progress.streakDays > 0 ? `${progress.streakDays}d racha` : "Sin racha"}
        </span>
        <span className="opacity-40">|</span>
        <span className="inline-flex items-center gap-1 tabular-nums">
          <Clock className="w-3.5 h-3.5 text-sky-400/90" />
          {formatPracticeDuration(progress.totalPracticeTime)}
        </span>
      </div>
      <div
        className="flex items-center gap-2 px-3 py-2 rounded-xl"
        style={{ backgroundColor: bg, border: `1px solid ${border}` }}
      >
        <Search className="w-4 h-4 shrink-0" style={{ color: textDim }} />
        <input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Buscar ejercicio…"
          className="bg-transparent outline-none text-sm w-36 sm:w-44"
          style={{ color: textMain }}
        />
      </div>
      </div>
    </div>
  );
}

function LevelChip({
  level,
  levelIndex,
  progress,
  active,
  onSelect,
  pathMode = false,
}: {
  level: Level;
  levelIndex: number;
  progress: UserProgress;
  active: boolean;
  onSelect: () => void;
  pathMode?: boolean;
}) {
  const unlocked = isLevelUnlocked(levelIndex, progress);
  const completed = isLevelCompleted(level, progress);
  const done = getCompletedCount(level, progress);
  const accent = pathMode ? "#99f6e4" : level.color;
  const bgCard = pathMode ? PATH.cardBg : GYM_STUDIO.bgCard;
  const bgHover = pathMode ? "rgba(255,255,255,0.04)" : GYM_STUDIO.bgCardHover;
  const border = pathMode ? PATH.cardBorder : GYM_STUDIO.border;
  const textMain = pathMode ? PATH.text : GYM_STUDIO.textMain;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;
  const bgElevated = pathMode ? PATH.cardBg : GYM_STUDIO.bgElevated;

  return (
    <button
      type="button"
      disabled={!unlocked}
      onClick={onSelect}
      className="flex items-center gap-2 px-3 py-2 rounded-xl text-left transition-all shrink-0"
      style={{
        backgroundColor: active ? bgHover : bgCard,
        border: `1px solid ${active ? `${accent}55` : border}`,
        opacity: unlocked ? 1 : 0.45,
        cursor: unlocked ? "pointer" : "not-allowed",
      }}
    >
      <span
        className="w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black shrink-0"
        style={{
          backgroundColor: completed ? "rgba(34,197,94,0.2)" : active ? accent : bgElevated,
          color: completed ? "#86efac" : active ? (pathMode ? "#100f0e" : "#fff") : textDim,
        }}
      >
        {completed ? <Check className="w-3.5 h-3.5" /> : !unlocked ? <Lock className="w-3 h-3" /> : levelIndex + 1}
      </span>
      <span className="text-xs font-semibold truncate max-w-[120px]" style={{ color: textMain }}>
        {level.name.replace(/^Nivel \d+: /, "")}
      </span>
      <span className="text-[10px] tabular-nums" style={{ color: textDim }}>
        {done}/{level.exercises.length}
      </span>
    </button>
  );
}

function LevelHero({
  level,
  progress,
  pathMode = false,
}: {
  level: Level;
  progress: UserProgress;
  pathMode?: boolean;
}) {
  const done = getCompletedCount(level, progress);
  const pct = level.exercises.length ? (done / level.exercises.length) * 100 : 0;
  const accent = pathMode ? "#99f6e4" : level.color;
  const bg = pathMode ? PATH.cardBg : GYM_STUDIO.bgCard;
  const border = pathMode ? PATH.cardBorderMint : GYM_STUDIO.border;
  const textMain = pathMode ? PATH.text : GYM_STUDIO.textMain;
  const textMuted = pathMode ? PATH.textMuted : GYM_STUDIO.textMuted;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;
  const trackBg = pathMode ? PATH.progressTrack : GYM_STUDIO.bgElevated;

  return (
    <div
      className={`rounded-2xl p-5 sm:p-6 ${pathMode ? "xl:sticky xl:top-4" : "max-w-7xl mx-auto"}`}
      style={{
        backgroundColor: bg,
        border: `1px solid ${border}`,
        backgroundImage: pathMode
          ? "linear-gradient(135deg, rgba(153, 246, 228, 0.08), transparent 55%)"
          : `linear-gradient(135deg, ${level.color}18, transparent 55%)`,
      }}
    >
      <p className="text-[10px] font-bold uppercase tracking-wider mb-1" style={{ color: accent }}>
        {pathMode ? "Camino principiante · método chileno" : level.methodChapter}
      </p>
      <h2 className="text-lg sm:text-xl font-serif font-light mb-1" style={{ color: textMain }}>
        {pathMode ? level.tagline : level.name}
      </h2>
      {!pathMode && (
        <p className="text-sm mb-3 max-w-2xl" style={{ color: textMuted }}>
          {level.tagline}
        </p>
      )}
      <p className="text-xs leading-relaxed max-w-3xl mb-4" style={{ color: textDim }}>
        {level.description}
      </p>
      <div className="flex items-center gap-3">
        <div className="flex-1 max-w-xs h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: trackBg }}>
          <div
            className="h-full rounded-full transition-all duration-500"
            style={{
              width: `${pct}%`,
              background: pathMode ? PATH.progressFill : accent,
            }}
          />
        </div>
        <span className="text-xs font-medium tabular-nums" style={{ color: textMuted }}>
          {done}/{level.exercises.length} completados
        </span>
      </div>
    </div>
  );
}

function ExerciseCard({
  exercise,
  levelColor,
  completed,
  score,
  onOpen,
  pathMode = false,
}: {
  exercise: Level["exercises"][number];
  levelColor: string;
  completed: boolean;
  score?: number;
  onOpen: () => void;
  pathMode?: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  const bgCard = pathMode ? PATH.cardBg : GYM_STUDIO.bgCard;
  const bgHover = pathMode ? "rgba(255,255,255,0.04)" : GYM_STUDIO.bgCardHover;
  const border = pathMode ? PATH.cardBorder : GYM_STUDIO.border;
  const borderHover = pathMode ? PATH.cardBorderMint : GYM_STUDIO.borderHover;
  const textMain = pathMode ? PATH.text : GYM_STUDIO.textMain;
  const textMuted = pathMode ? PATH.textMuted : GYM_STUDIO.textMuted;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;
  const playAccent = pathMode ? "#99f6e4" : GYM_STUDIO.accent;
  const playGlow = pathMode ? "rgba(153, 246, 228, 0.35)" : GYM_STUDIO.accentGlow;

  return (
    <motion.button
      type="button"
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.96 }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onOpen}
      className="relative text-left rounded-2xl overflow-hidden transition-all"
      style={{
        backgroundColor: hovered ? bgHover : bgCard,
        border: `1px solid ${hovered ? borderHover : border}`,
      }}
    >
      {!pathMode && (
      <div
        className="h-[2px] w-full transition-all"
        style={{
          background: hovered ? `linear-gradient(90deg, ${levelColor}, transparent)` : "transparent",
        }}
      />
      )}
      <div className="p-4 md:p-5">
        <div className="flex items-start justify-between gap-2 mb-2">
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black"
            style={{ backgroundColor: `${levelColor}22`, color: levelColor }}
          >
            {exercise.order}
          </span>
          {completed && (
            <span className="flex items-center gap-1 text-[10px] font-semibold text-emerald-400/90">
              <Check className="w-3 h-3" />
              {score != null ? `${score} pts` : "Hecho"}
            </span>
          )}
        </div>
        <h3 className="font-serif font-light text-base mb-0.5 line-clamp-2" style={{ color: textMain }}>
          {exercise.title}
        </h3>
        <p className="text-xs line-clamp-2 mb-3" style={{ color: textMuted }}>
          {exercise.subtitle}
        </p>
        <div className="flex flex-wrap gap-1">
          <Badge label={getDifficultyLabel(exercise.difficulty)} pathMode={pathMode} />
          <Badge label={getCategoryLabel(exercise.category)} muted pathMode={pathMode} />
          <Badge label={getTypeLabel(exercise.type)} muted pathMode={pathMode} />
        </div>
        <p className="text-[10px] mt-3 flex items-center gap-1" style={{ color: textDim }}>
          <Clock className="w-3 h-3" />~{exercise.timeEstimate} min
        </p>
      </div>

      <AnimatePresence>
        {hovered && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
            style={{ backgroundColor: pathMode ? "rgba(16,15,14,0.55)" : "rgba(15,15,18,0.65)" }}
          >
            <div
              className="w-12 h-12 rounded-full flex items-center justify-center"
              style={{
                background: pathMode ? PATH.btnMint : playAccent,
                boxShadow: `0 0 20px ${playGlow}`,
              }}
            >
              <Play className="w-5 h-5 text-[#100f0e] ml-0.5" fill="#100f0e" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  );
}

function Badge({
  label,
  muted,
  pathMode = false,
}: {
  label: string;
  muted?: boolean;
  pathMode?: boolean;
}) {
  const bgElevated = pathMode ? PATH.cardBg : GYM_STUDIO.bgElevated;
  const accentSoft = pathMode ? "rgba(153, 246, 228, 0.12)" : GYM_STUDIO.accentSoft;
  const accent = pathMode ? "#99f6e4" : GYM_STUDIO.accent;
  const textDim = pathMode ? PATH.textDim : GYM_STUDIO.textDim;

  return (
    <span
      className="text-[9px] px-1.5 py-0.5 rounded font-medium"
      style={{
        backgroundColor: muted ? bgElevated : accentSoft,
        color: muted ? textDim : accent,
      }}
    >
      {label}
    </span>
  );
}

export default GymLibrary;
